// Warm-up set logic.
//
// Rule:
//  - For the FIRST exercise of a workout day that loads a given target muscle group,
//    do 2 warm-up sets: 60% × 8 reps, then 90% × 4 reps (of the first working set weight).
//  - For SUBSEQUENT exercises whose target muscle group has already been primed by an
//    earlier exercise in the same day, do 1 warm-up set: 75% × 6 reps.
//
// "Primed" means the muscle was either the PRIMARY mover of a previous exercise,
// or a heavily-recruited secondary mover (e.g. bench press primes triceps + shoulders).

// Pattern → muscles that get sufficiently primed by training that movement pattern.
// Conservative on secondaries — we only list muscles that are warm enough after
// a heavy compound set to skip an extra warm-up later in the session.
const PATTERN_MUSCLES = {
    horizontal_push: ["chest", "triceps", "shoulders"],
    vertical_push:   ["shoulders", "triceps"],
    horizontal_pull: ["back", "biceps"],
    vertical_pull:   ["back", "biceps"],
    squat:           ["quads", "glutes", "hamstrings"],
    hip_hinge:       ["hamstrings", "glutes", "back"],
    hip_extension:   ["glutes", "hamstrings"],
    knee_extension:  ["quads"],
    knee_flexion:    ["hamstrings"],
    hip_abduction:   ["glutes"],
    hip_adduction:   ["adductors"],
    ankle_extension: ["calves"],
    elbow_extension: ["triceps"],
    elbow_flexion:   ["biceps"],
    lateral_raise:   ["shoulders"],
    rear_delt:       ["shoulders", "back"],
    trunk_flexion:   ["abs"],
};

// Each exercise's "primary target" for warmup-priming purposes. We use the
// exercise's stored muscle_group when available, otherwise fall back to the
// pattern's first muscle in PATTERN_MUSCLES.
function primaryMuscleFor(exercise) {
    if (exercise?.muscle_group) return exercise.muscle_group;
    const pm = PATTERN_MUSCLES[exercise?.pattern || ""] || [];
    return pm[0] || "other";
}

// All muscles primed by training this exercise (primary + heavily-recruited secondaries).
// Returns [] for unknown/missing patterns so an unknown exercise primes NOTHING
// (and therefore won't accidentally cause subsequent exercises to drop to 1 warmup).
function musclesPrimedBy(exercise) {
    const p = exercise?.pattern;
    if (p && PATTERN_MUSCLES[p]) return PATTERN_MUSCLES[p];
    return [];
}

/**
 * Compute the warm-up set TEMPLATES for an exercise based on its order in the day.
 * Returns an array of template objects: [{ target_pct, reps }].
 *
 * @param {Array} dayExercises – ordered exercises in the workout day
 * @param {number} index – position of the exercise to compute warmups for
 * @returns {Array<{target_pct:number, reps:number}>}
 */
export function warmupTemplatesFor(dayExercises, index) {
    if (!Array.isArray(dayExercises) || index < 0 || index >= dayExercises.length) return [];
    const current = dayExercises[index];
    if (!current) return [];
    const target = primaryMuscleFor(current);

    // Collect all muscles primed by earlier exercises
    const primed = new Set();
    for (let i = 0; i < index; i++) {
        for (const m of musclesPrimedBy(dayExercises[i])) primed.add(m);
    }

    if (primed.has(target)) {
        return [{ target_pct: 75, reps: 6 }];
    }
    return [
        { target_pct: 60, reps: 8 },
        { target_pct: 90, reps: 4 },
    ];
}

/**
 * Build full warm-up sets from templates + the user's first working-set weight.
 * Pre-fills `weight` to the rounded suggested weight; user can override.
 */
export function buildWarmupsFromTemplates(templates, firstWorkingWeight) {
    const w = Number(firstWorkingWeight) || 0;
    return templates.map(t => ({
        target_pct: t.target_pct,
        reps: t.reps,
        weight: w > 0 ? Math.round((w * t.target_pct) / 100) : 0,
    }));
}

/**
 * Ensure an exercise has a `warmup_sets` array populated. Returns a NEW array
 * (does not mutate). Behavior:
 *  - If `warmup_sets` already exists with the right count, return as-is.
 *  - Otherwise rebuild from templates + the first working-set weight (or 0 if blank).
 */
export function ensureWarmups(exercise, dayExercises, index) {
    const templates = warmupTemplatesFor(dayExercises, index);
    const existing = Array.isArray(exercise?.warmup_sets) ? exercise.warmup_sets : [];
    if (existing.length === templates.length && existing.every((s, i) => s.target_pct === templates[i].target_pct && s.reps === templates[i].reps)) {
        return existing;
    }
    const firstWeight = exercise?.sets?.[0]?.weight ?? 0;
    return buildWarmupsFromTemplates(templates, firstWeight);
}
